import {
    InfiniteData,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import {
    acknowledge,
    getNotificationSummary,
    getNotifications,
    markAllAsRead,
    markAsRead,
} from '../service';
import { NotificationSummary, PaginatedNotifications } from '../types';

function useNotifications() {
    const query = useInfiniteQuery({
        queryKey: ['notifications', 'all'],
        queryFn: ({ pageParam }) => getNotifications(pageParam),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
        staleTime: 0,
        refetchOnWindowFocus: true,
    });

    const notifications = query.data?.pages.flatMap((page) => page.notifications) ?? [];

    return {
        ...query,
        notifications,
    };
}

function useNotificationSummary() {
    return useQuery({
        queryKey: ['notifications', 'summary'],
        queryFn: getNotificationSummary,
        staleTime: 0,
        refetchOnWindowFocus: true,
    });
}

function useAcknowledge() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: acknowledge,
        onMutate: () => {
            // Reset badge count only — unread state is independent of acknowledgement
            queryClient.setQueryData<NotificationSummary>(
                ['notifications', 'summary'],
                (current) => ({
                    unseenCount: 0,
                    unreadCount: current?.unreadCount ?? 0,
                })
            );
        },
        onError: () => {
            void queryClient.invalidateQueries({ queryKey: ['notifications', 'summary'] });
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: ['notifications', 'summary'] });
        },
    });
}

function setAllNotificationsRead(
    current: InfiniteData<PaginatedNotifications> | undefined,
    predicate: (id: string) => boolean
): InfiniteData<PaginatedNotifications> | undefined {
    if (current === undefined) return current;

    return {
        ...current,
        pages: current.pages.map((page) => ({
            ...page,
            notifications: page.notifications.map((notification) => (
                predicate(notification.id)
                    ? { ...notification, isRead: true }
                    : notification
            )),
        })),
    };
}

function useMarkAsRead() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationFn: (id: string) => markAsRead(id),
        onMutate: (id: string) => {
            const previousNotifications = queryClient
                .getQueryData<InfiniteData<PaginatedNotifications>>(['notifications', 'all']);

            queryClient.setQueryData<InfiniteData<PaginatedNotifications>>(
                ['notifications', 'all'],
                (current) => setAllNotificationsRead(
                    current,
                    (notificationId) => notificationId === id
                )
            );

            return { previousNotifications };
        },
        onError: (_error, _id, context) => {
            if (context?.previousNotifications !== undefined) {
                queryClient.setQueryData(['notifications', 'all'], context.previousNotifications);
            }
            toast({ title: 'Failed to mark notification as read, please try again.', status: 'error' });
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: ['notifications', 'all'] });
            void queryClient.invalidateQueries({ queryKey: ['notifications', 'summary'] });
        },
    });
}

function useMarkAllAsRead() {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationFn: markAllAsRead,
        onMutate: () => {
            const previousNotifications = queryClient
                .getQueryData<InfiniteData<PaginatedNotifications>>(['notifications', 'all']);
            const previousSummary = queryClient
                .getQueryData<NotificationSummary>(['notifications', 'summary']);

            queryClient.setQueryData<InfiniteData<PaginatedNotifications>>(
                ['notifications', 'all'],
                (current) => setAllNotificationsRead(current, () => true)
            );
            queryClient.setQueryData<NotificationSummary>(
                ['notifications', 'summary'],
                (current) => ({
                    unseenCount: current?.unseenCount ?? 0,
                    unreadCount: 0,
                })
            );

            return { previousNotifications, previousSummary };
        },
        onError: (_error, _variables, context) => {
            if (context?.previousNotifications !== undefined) {
                queryClient.setQueryData(['notifications', 'all'], context.previousNotifications);
            }
            if (context?.previousSummary !== undefined) {
                queryClient.setQueryData(['notifications', 'summary'], context.previousSummary);
            }
            toast({ title: 'Failed to mark all notifications as read, please try again.', status: 'error' });
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: ['notifications', 'all'] });
            void queryClient.invalidateQueries({ queryKey: ['notifications', 'summary'] });
        },
    });
}

export {
    useNotifications,
    useNotificationSummary,
    useAcknowledge,
    useMarkAsRead,
    useMarkAllAsRead,
};
